import qs from 'query-string'
import type { IDashboardFilter, IRequestModelFieldnameSuggestions } from '@looker/sdk'
import type { TagsSearchResponse } from '@zendesk/zqa-services/tickets'
import type { LookerCookielessSession, LookerSdkSession } from '@zendesk/zqa-services/users'

import { api } from '@/api'
import { session } from '@/composables/useSession'

import type { InitialAssignmentFilter, LookerDashboard } from '../looker/types'
import type { FilterParams, Member } from '../types'

export type CoreParams = Pick<FilterParams, 'workspaceIds'>

export type SearchUsersReturn = Promise<{
  users: Member[]
  total: number
}>

export const searchUsers = async ({
  workspaceIds,
  name,
  ids,
  limit = 50,
  groupIds,
  signal,
}: {
  workspaceIds?: number[]
  name?: string
  ids?: number[]
  limit?: number
  groupIds?: string[]
  signal?: AbortSignal
}): Promise<SearchUsersReturn> => {
  const { users, total } = await api
    .get('users/search', {
      headers: workspaceIds ? { 'X-Klaus-Workspace': workspaceIds.join() } : {},
      searchParams: qs.stringify({ name, ids, limit, groupIds }),
      signal,
    })
    .json<{ users: { id: string; name: string; email: string; avatar: string }[]; total: string }>()

  return { users: users.map((u) => ({ ...u, id: parseInt(u.id) })), total: parseInt(total) }
}

export const searchTicketTags = ({
  workspaceIds,
  tag,
  signal,
}: CoreParams & { tag?: string } & { signal?: AbortSignal }) =>
  api
    .get('ticket/tags/prefix', {
      headers: { 'X-Klaus-Workspace': workspaceIds.join() },
      searchParams: qs.stringify({ tag, limit: 100 }),
      signal,
    })
    .json<TagsSearchResponse>()

export const searchAccountTicketTags = ({ tag, signal }: { tag?: string; signal?: AbortSignal }) =>
  api
    .get('ticket/account/tags/prefix', {
      searchParams: qs.stringify({ tag, limit: 100 }),
      signal,
    })
    .json<TagsSearchResponse>()

export const getLookerDashboards = () =>
  api
    .get('looker/dashboards', { headers: { 'X-Klaus-Workspace': session.workspace.id.toString() } })
    .json<{ dashboards: LookerDashboard[]; lookerRegionalInstance?: string }>()

export const getInitialAssignmentFilterData = async () => {
  return await api
    .get('assignments/v2/looker/filter', { headers: { 'X-Klaus-Workspace': session.workspace.id.toString() } })
    .json<InitialAssignmentFilter>()
}

export const getLookerSession = (id: string) =>
  api
    .get('looker/session', { searchParams: { dashboardId: id } })
    .json<LookerCookielessSession>()
    .then((session) => ({
      authentication_token: session.authenticationToken,
      authentication_token_ttl: Number(session.authenticationTokenTtl),
      navigation_token: session.navigationToken,
      navigation_token_ttl: Number(session.navigationTokenTtl),
      api_token: session.apiToken,
      api_token_ttl: Number(session.apiTokenTtl),
      // Needed for embed SDK
      session_reference_token_ttl: NaN,
    }))

export const getLookerSDKToken = (id: string) =>
  api
    .get('looker/sdk', { searchParams: { dashboardId: id } })
    .json<LookerSdkSession>()
    .then((tokenResponse) => ({
      access_token: tokenResponse.accessToken,
      token_type: tokenResponse.tokenType,
      expires_in: tokenResponse.expiresIn != null ? parseInt(tokenResponse.expiresIn, 10) : null,
      refresh_token: tokenResponse.refreshToken,
    }))

export const getLookerDashboardFilters = (id: string) =>
  api.get(`looker/dashboards/${id}/filters`).json<{ dashboard_filters: IDashboardFilter[] }>()

export const getLookerFilterFieldSuggestions = ({
  model_name,
  view_name,
  field_name,
  term,
  filters,
}: IRequestModelFieldnameSuggestions) =>
  api
    .get(`looker/models/${model_name}/views/${view_name}/fields/${field_name}/suggestions`, {
      searchParams: qs.stringify({ term, filters }),
    })
    .json()
